/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { Box, debounce, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ExpenseTypeService from "../../services/expenseTypeService";
import Popup from "../../components/popups/popup";
import UpdateExpenseTypeBox from "../../components/boxes/expenseType/updateExpenseTypeBox";
import AddExpenseTypeBox from "../../components/boxes/expenseType/addExpenseTypeBox";
import SearchInput from "../../components/inputs/searchInput";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DeleteExpenseTypeModal from "../../components/modals/expenseType/deleteExpenseTypeModal";
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from "../../components/inputs/onOffSwitchForDeleted";

const ExpenseType = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [expenseTypes, setExpenseTypes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedExpenseType, setSelectedExpenseType] = useState({});
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);


    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };


    const handleOpenUpdate = (expenseType) => {
        setSelectedExpenseType(expenseType);
        setOpenUpdate(true);
    };

    const handleReload = async () => {
        setOpenUpdate(false);
        setHasMore(true);
        setPage(1);
        setSearchTerm('');
        await fetchExpenseTypes();
    };

    const handleOpenDelete = (expenseType) => {
        setSelectedExpenseType(expenseType);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            await fetchExpenseTypes();
        }
    };


    const fetchExpenseTypes = async (page = 1, name = '') => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = { name, deleted, ...sortState };
            const data = await ExpenseTypeService.fetch(page, filters);

            if (data.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setExpenseTypes([]);
                }
            } else {
                setExpenseTypes((prevs) => (page === 1 ? data : [...prevs, ...data]));
            }
        } catch (error) {
            console.error('Error fetching expense types:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchExpenseTypes = useMemo(() =>
        debounce((name) => {
            setPage(1);
            setHasMore(true);
            fetchExpenseTypes(1, name);
        }, 1000),
        []
    );


    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchExpenseTypes(value);
    };

    const resetFilter = () => {
        setSearchTerm('');
        setHasMore(true);
        if (page !== 1) setPage(1);
        else fetchExpenseTypes(1, '');
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchExpenseTypes(page, searchTerm);
    }, [page]);

    useEffect(() => {
        if (!isFirstRender.current) resetFilter();
    }, [sortState, deleted]);


    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchExpenseTypes(page + 1, searchTerm);
                setPage(prevPage => prevPage + 1);
            }
        }
    };

    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteExpenseTypeModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} expenseType={selectedExpenseType} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {isTablet && <Box>
                {
                    (selectedExpenseType && openUpdate) ?
                        <UpdateExpenseTypeBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} expenseType={selectedExpenseType} />
                        :
                        <AddExpenseTypeBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} />
                }
            </Box>}

            {/* Filter row */}
            {
                isTablet ?
                    <Box
                        sx={{
                            my: 2,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{ px: 2 }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={isTablet ? '60%' : '70%'}>
                                <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                            </Box>
                        </Box>
                        <Divider />

                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{
                                ml: isEn ? 3 : 0
                            }}>
                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                            </Box>
                            <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    :
                    <Box
                        sx={{
                            my: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={isTablet ? '70%' : '80%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            <Box sx={{
                display: 'flex'
            }}>
                {!isTablet && <Box sx={{
                    width: '35%'
                }}>
                    {
                        (selectedExpenseType && openUpdate) ?
                            <UpdateExpenseTypeBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} expenseType={selectedExpenseType} />
                            :
                            <AddExpenseTypeBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} />
                    }
                </Box>}

                {!isTablet && <Box width={10}></Box>}

                {/* Scrollable Table with Sticky Header */}
                <TableContainer
                    component={Paper}
                    onScroll={handleScroll}
                    ref={tableContainerRef}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: 'calc(100vh - 170px)',
                        overflowY: 'auto',
                        width: isTablet ? '100%' : '65%'
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('name')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('name')} {sortState.sortBy === 'name' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {expenseTypes.map((expenseType, index) => (
                                <TableRow
                                    key={expenseType.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{expenseType.id}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{expenseType.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            border: 1,
                                            borderColor: theme.palette.customGrey.borderInput,
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: 1,
                                        }}>
                                            {expenseType.deletedAt === null && <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenUpdate(expenseType)}>
                                                <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>}
                                            {expenseType.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenDelete(expenseType)}>
                                                {expenseType.deletedAt === null ?
                                                    <DeleteOutlineOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.red.bg }} />
                                                    :
                                                    <ReplayOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.green.bg }} />
                                                }
                                            </Box>

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>

        </Box>
    );
}

export default ExpenseType;